// Monogramm „Bar“ (DESIGN §3.17, §4.6, N6): einfarbige Kachel wie die Primär-Pill (dunkel weiß mit
// schwarzem Buchstaben, hell schwarz mit weißem), kein Verlauf. Buchstabe aus APP_NAME, 60 % der Höhe,
// Gewicht 700. Plattform-Kennung als Punkt (kompakt) oder als Badge neben der Wortmarke (`PlatformBadge`).
import { type ComponentPropsWithRef } from 'react';
import { cn } from './cn';
import { cva, type VariantProps } from './cva';

export type Platform = 'people' | 'admin' | 'ai';

const monogramVariants = cva(
  'relative inline-flex shrink-0 select-none items-center justify-center bg-primary leading-none font-bold text-on-primary',
  {
    variants: {
      size: {
        24: 'size-6 rounded-sm',
        28: 'size-7 rounded-md',
        32: 'size-8 rounded-md',
        36: 'size-9 rounded-md',
        40: 'size-10 rounded-md',
      },
    },
    defaultVariants: { size: 36 },
  },
);

const dotVariants = {
  admin: 'bg-admin',
  ai: 'bg-ai',
} as const;

export type MonogramProps = Omit<ComponentPropsWithRef<'span'>, 'children'> &
  VariantProps<typeof monogramVariants> & {
    /** Name der App (APP_NAME); der erste Buchstabe ist das Monogramm. */
    appName?: string;
    /** Plattform-Punkt unten rechts (Admin neutral, AI Akzent). */
    platform?: Platform;
    /** Zugänglicher Name; ohne Angabe dekorativ. */
    label?: string;
  };

export function Monogram({
  size,
  appName = 'Bar',
  platform,
  label,
  className,
  style,
  ...rest
}: MonogramProps) {
  const letter = (appName.trim()[0] ?? 'B').toUpperCase();
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-platform={platform ?? 'people'}
      className={monogramVariants({ size, className })}
      style={{ fontSize: Math.round(Number(size ?? 36) * 0.6), ...style }}
      {...rest}
    >
      <span aria-hidden>{letter}</span>
      {platform && platform !== 'people' ? (
        <span
          aria-hidden
          className={cn(
            'absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-page',
            dotVariants[platform],
          )}
        />
      ) : null}
    </span>
  );
}

const badgeVariants = cva('inline-flex h-5 shrink-0 items-center rounded-full px-2 text-chip', {
  variants: {
    platform: {
      admin: 'bg-admin text-on-admin',
      ai: 'bg-ai-soft text-ai-fg',
    },
  },
});

/** Badge „Admin“ bzw. „AI“ neben der Wortmarke (DESIGN §3.2.2, §3.3.2). */
export function PlatformBadge({
  platform,
  className,
}: {
  platform: 'admin' | 'ai';
  className?: string;
}) {
  return (
    <span data-platform={platform} className={badgeVariants({ platform, className })}>
      {platform === 'admin' ? 'Admin' : 'AI'}
    </span>
  );
}

export interface PlatformMarkProps {
  platform: Platform;
  appName?: string;
  size?: 24 | 28 | 32 | 36;
  className?: string;
}

/** Monogramm + Wortmarke + Plattform-Badge (Kopf von AdminNav, AiSidebar, Auth). */
export function PlatformMark({
  platform,
  appName = 'Bar',
  size = 32,
  className,
}: PlatformMarkProps) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <Monogram size={size} appName={appName} />
      <span className="truncate text-label-strong text-fg">{appName}</span>
      {platform !== 'people' ? <PlatformBadge platform={platform} /> : null}
    </span>
  );
}
